<template>
    <div class="EidIcon">
        <inline-svg
            v-if="defaultIconSvg && isIconError"
            class="EidIcon__svg EidIcon__svg--default"
            v-bind="svgProps"
            :src="defaultIconSvg"
        />
        <inline-svg
            v-else-if="iconSrc && iconType === 'svg'"
            class="EidIcon__svg"
            v-bind="svgProps"
            :src="iconSrc"
            @error="onIconError"
        />
        <FontAwesomeIcon
            v-else-if="iconSrc && iconType === 'fontAwesome'"
            v-bind="iconProps"
            :icon="iconSrc"
            @error="onIconError"
        />
        <img
            v-else
            :src="iconSrc"
            :alt="alt"
            @error="onIconError"
        >
    </div>
</template>

<style lang="scss">
@import "./index.scss";
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    // PRODUCT-26110 Task 8b Step 5: vue-inline-svg 2 (Options API, Vue-2-only) -> 3 (the
    // same package's Vue 3 release line, same default export/props/events). v3's own
    // `render()` explicitly merges `this.$attrs` (which in Vue 3 always includes
    // class/style) onto the rendered `<svg>` root itself -- it doesn't depend on the
    // INSTANCE_ATTRS_CLASS_STYLE auto-merge compat behavior the way vuedraggable 2 did.
    import InlineSvg from 'vue-inline-svg';

    import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { EidIconFontAwesomeProps, EidIconInlineSvgProps } from './types';

    type iconType = 'image' | 'svg' | 'fontAwesome';

    export default defineComponent({
        name: 'EidIcon',
        components: {
            InlineSvg,
            FontAwesomeIcon,
        },
        // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): the root `<div>` used to have its
        // own `@click="$emit('click', $event)"`, manually re-emitting a native click as
        // a component event -- necessary pre-Vue-3, since `$attrs` excluded listeners
        // and a caller's `@click` never reached this div any other way. Vue 3 merges
        // listeners into `$attrs`, so plain default attrs fallthrough (inheritAttrs
        // stays default here, unlike most of this sweep) now forwards a caller's
        // `onClick` here automatically -- keeping the manual re-emit alongside it
        // double-fired the caller's handler per click (found via Policy.spec's
        // Expandable-menu toggle button, whose `toggle()` flipped closed again after one
        // click). Removed as redundant rather than gating with `inheritAttrs: false`,
        // since ~100 call sites rely on `data-test-id`/other non-class attrs reaching
        // this div via that same automatic fallthrough.
        props: {
            iconSrc: {
                type: [ String, Object ] as PropType<string | IconDefinition>,
                default: '',
            },
            // in case of icon failed on load, defaultIconSvg meant to use an svg that is stored within the project.
            defaultIconSvg: {
                type: [ String, Object ] as PropType<string | IconDefinition>,
                default: '',
            },
            alt: {
                type: String,
                default: 'icon',
            },
            iconProps: {
                type: Object as PropType<EidIconFontAwesomeProps>,
                default: (): EidIconFontAwesomeProps => ({}),
            },
            svgProps: {
                type: Object as PropType<EidIconInlineSvgProps>,
                default: (): EidIconInlineSvgProps => ({}),
            },
        },
        data() {
            return {
                isIconError: false,
            };
        },
        computed: {
            iconType(): iconType {
                if (typeof this.iconSrc !== 'string') {
                    return 'fontAwesome';
                }
                // An SVG small enough to fall under the bundler's inlining threshold arrives
                // as a data URI rather than a path, and a path can carry a cache-busting
                // query. Both still name an SVG, and both have to render inline: an <img>
                // draws the file in its own context, where `fill="currentColor"` cannot see
                // the colour set on the page and the icon sizes as a replaced element.
                if (this.iconSrc.startsWith('data:image/svg+xml')) {
                    return 'svg';
                }
                return this.iconSrc.split(/[?#]/)[0].endsWith('.svg') ? 'svg' : 'image';
            },
        },
        methods: {
            onIconError() {
                this.isIconError = true;
            },
        },
    });
</script>
